import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import type { FilterModel } from 'ag-grid-community';
import type { SelectOption } from '@/formbuilder/SelectFormComponent';
import type { AGGridCrossFilterChartTileProps } from '@/impactReports/tiles/AGGridCrossFilterChartTile.atom';
import type { AGGridTableTileProps } from '@/impactReports/tiles/AGGridTableTile.atom';
import type { GrayBackgroundTitleAndNumberTileProps } from '@/impactReports/tiles/GrayBackgroundTitleAndNumberTileProps.atom';
import type { LabelCategoryOutputV1 } from '@seeqdev/api/model/LabelCategoryOutputV1';

export const IMPACT_OWNER_FIRSTNAME = 'Impact Owner.properties.First Name';
export const IMPACT_OWNER_LASTNAME = 'Impact Owner.properties.Last Name';
export const MANAGER_ID = 'managerId';
export const IS_MANAGER = 'isManager';
export const MANAGER_FIRSTNAME = 'Manager.properties.First Name';
export const MANAGER_LASTNAME = 'Manager.properties.Last Name';
export const REVIEWED_BY_FIRSTNAME = 'Reviewed By.properties.First Name';
export const REVIEWED_BY_LASTNAME = 'Reviewed By.properties.Last Name';

export const IMPACT_REPORT_CATEGORY_DISPLAY_NAME = 'Category Display Name';
export const IMPACT_REPORT_LABEL_ID = 'Label Id';
export const IMPACT_REPORT_CATEGORY_PREFIX = SeeqNames.ImpactReport.CategoryPrefix;
export const IMPACT_REPORT_KPI_PREFIX = SeeqNames.ImpactReport.KPIPrefix;
export const IMPACT_REPORT_KPI_EXPECTED_ANNUAL_SUFFIX = '_expected_annual_savings';
export type Stages = 'identify' | 'categorize' | 'quantify' | 'review';
export const availableStages: Stages[] = ['identify', 'categorize', 'quantify', 'review'];
export const stageIcons = ['fa-lightbulb-on', 'fa-list', 'fa-piggy-bank', 'fa-circle-check'];
export const TRACK_IMPACT = 'Impact Report';
export type ImpactReportSelectedItemType = '' | 'Topic' | 'Analysis' | 'Vantage' | 'Folder' | 'Project';
const IMPACT_REPORT_SELECTABLE_ITEM_TYPES: Exclude<ImpactReportSelectedItemType, ''>[] = [
  SeeqNames.Types.Topic,
  SeeqNames.Types.Analysis,
  SeeqNames.Types.Vantage,
  SeeqNames.Types.Folder,
  SeeqNames.Types.Project,
];

export function toImpactReportSelectedItemType(itemType?: string | null): ImpactReportSelectedItemType {
  return itemType && (IMPACT_REPORT_SELECTABLE_ITEM_TYPES as string[]).includes(itemType)
    ? (itemType as ImpactReportSelectedItemType)
    : '';
}

export interface ImpactReportConfiguration {
  showImpact: boolean;
  showSite: boolean;
  showLocation: boolean;
  showAssetPath: boolean;
  expectedAnnualKpiVisibility?: Record<string, boolean>;
}

export interface ImpactReportLayout {
  id: string;
  name: string;
  layoutJson: string | null;
  columnDefinitions?: string | null;
  mapZoom?: string | null;
  filterModel?: string | null;
  ownerId?: string | null;
  isDefault: boolean;
}

export type ImpactReportFilterModel = FilterModel | null;

export interface ImpactReportMapStartEnd {
  start: number;
  end: number;
}

export interface MapZoomSettings {
  [gridItemId: string]: {
    ratioX: ImpactReportMapStartEnd;
    ratioY: ImpactReportMapStartEnd;
  };
}

export const OPEN_CREATE_MODAL_QUERY_PARAM = 'createImpact';
export const NOMINATE_IMPACT_QUERY_PARAM = 'nominateImpact';
export const IMPACT_REPORT_CONTEXT_CATEGORY = 'VCC_ImpactReportNominationCategory';
export const IMPACT_REPORT_NOMINATION_LABEL = 'VCC_ImpactReportNominationLabel';
export interface ImpactReportTile {
  type: 'chart' | 'table' | 'gauge' | 'box' | 'map' | 'markdown';
  w: number;
  h: number;
  x: number;
  y: number;
  i: string;
  id: string;
}

export type ImpactReportChartDefinition = ImpactReportTile &
  Partial<AGGridCrossFilterChartTileProps> & { type: 'chart' };
export type ImpactReportTableDefinition = ImpactReportTile & Partial<AGGridTableTileProps> & { type: 'table' };
export type ImpactReportBoxTileDefinition = ImpactReportTile &
  GrayBackgroundTitleAndNumberTileProps & {
    type: 'box';
    label?: string;
    format?: boolean;
  };
export type ImpactReportGaugeTileDefinition = ImpactReportTile & { type: 'gauge'; lookupColumn?: string };
export type ImpactReportMapTileDefinition = ImpactReportTile & { type: 'map' };
export type ImpactReportMarkdownTileDefinition = ImpactReportTile & { type: 'markdown'; markdown: string };

export type ImpactReportDashboardContent =
  | ImpactReportChartDefinition
  | ImpactReportTableDefinition
  | ImpactReportBoxTileDefinition
  | ImpactReportGaugeTileDefinition
  | ImpactReportMapTileDefinition
  | ImpactReportMarkdownTileDefinition;

// this should NEVER be used - but instead of having never block a customer we make sure they always have a table
export const LAST_DITCH_LAYOUT: ImpactReportDashboardContent[] = [
  {
    type: 'table',
    id: 'table',
    i: 'table',
    w: 12,
    x: 0,
    y: Number.MAX_SAFE_INTEGER,
    h: 40,
  },
];

export type PredefinedDateRangeOptions = 'today' | 'month' | 'quarter' | 'year' | 'yearToDate' | 'pastYear';
export type ImpactType = 'oneTime' | 'continuous' | 'customCalc';

export interface ImpactReportCategory {
  category: LabelCategoryOutputV1;
  options: SelectOption<string>[] | undefined;
}

export interface ImpactReportUseCase {
  [SeeqNames.ImpactReport.ImpactOwner]: string;
  [SeeqNames.ImpactReport.ImpactName]: string;
  [IMPACT_OWNER_FIRSTNAME]: string | undefined;
  [IMPACT_OWNER_LASTNAME]: string | undefined;
  [REVIEWED_BY_FIRSTNAME]?: string | undefined;
  [REVIEWED_BY_LASTNAME]?: string | undefined;
  [IS_MANAGER]?: boolean;
  [MANAGER_ID]?: string;
  [SeeqNames.ImpactReport.ImpactStatus]?: Stages;
  [SeeqNames.ImpactReport.AssignedToWorksheet]?: string;
  [SeeqNames.ImpactReport.AssignedToWorkbook]?: string;
  [SeeqNames.ImpactReport.ImpactBasedOn]?: string;
  [SeeqNames.MaterializedTables.DatumIdColumn]?: string;
  [SeeqNames.MaterializedTables.ItemIdColumn]?: string;
  [SeeqNames.ImpactReport.ImpactDescription]?: string;
  [SeeqNames.ImpactReport.ImpactStartDate]?: Date;
  [SeeqNames.ImpactReport.ImpactEndDate]?: Date;
  [SeeqNames.ImpactReport.ReviewComment]?: string;
  [SeeqNames.ImpactReport.ReviewDate]?: Date | string;
  [SeeqNames.ImpactReport.ReviewedBy]?: string;
  [SeeqNames.ImpactReport.ImpactSavingsType]?: string;
  [SeeqNames.ImpactReport.ImpactFormulaParam]?: string;
  [SeeqNames.ImpactReport.ImpactFrequency]?: string;
  [SeeqNames.ImpactReport.Location]?: string;
  [SeeqNames.ImpactReport.ImpactSite]?: string;
}

export const instructions =
  '<p>The <strong>Description</strong> field should clearly describe the technical problem or challenge your team is facing. This may include issues with systems, processes, or equipment. Be as specific as possible, focusing on the following points:</p><ol class="ak-ol" data-indent-level="2" start="1"><li><p><strong data-renderer-mark="true">Problem Overview</strong>: Provide a concise explanation of the issue you are trying to solve. Mention the system, process, or equipment involved, and explain the challenge (e.g., pressure fluctuations, overloads, nuisance alarms).</p></li><li><p data-renderer-start-pos="3269"><strong>Impact</strong>: Describe the potential or actual negative impact of the issue. This could include safety risks, financial costs, production delays, or regulatory concerns.</p></li><li><p data-renderer-start-pos="3436"><strong>Specific Technical Details</strong>: Include any technical metrics or specifications related to the issue (e.g., pressure thresholds, system overloads, number of alarms, temperature limits). Be as detailed as possible so the problem is clearly understood.</p></li><li><p><strong data-renderer-mark="true">Action Items</strong>: Outline the steps or actions that will be taken to address the technical challenge. Specify who is responsible for each action, deadlines, and any tools or resources required for resolution or implementation.</p></li></ol>';

export interface FlaggedItem {
  name: string;
  workbookId?: string;
  worksheetId: string;
  owner?: string;
  labelId?: string;
}

export interface Location {
  placeId: string;
  label: string;
  longitude?: number;
  latitude?: number;
}

export const quantifyUnits = {
  Time: ['d', 'h', 's', 'min'],
  Currency: ['$', 'M$', 'M£', 'M¤', 'M¥', 'M₩', 'M€', 'k$', 'k£', 'k¤', 'k¥', 'k₩', 'k€', '£', '¤', '¥', '₩', '€'],
  Energy: [
    'BTU',
    'GJ',
    'GW·h',
    'J',
    'MBTU',
    'MJ',
    'MW·h',
    'Mcal',
    'W·h',
    'W·s',
    'cal',
    'ftlb',
    'kBTU',
    'kJ',
    'kW·h',
    'kcal',
  ],
  Quantity: ['count'],
  Mass: ['Mlb', 'g', 'kg', 'klb', 'lb', 'lton', 'ston', 't'],
  Volume: [
    'L',
    'ML',
    'Mbbl',
    'Mcf',
    'Mgal',
    'bbl',
    'cf',
    'cm³',
    'dL',
    'gal',
    'hL',
    'kL',
    'kbbl',
    'kcf',
    'kgal',
    'mL',
    'm³',
    'nL',
  ],
  Area: ['acre', 'cm²', 'ft²', 'ha', 'in²', 'km²', 'mi²', 'mm²', 'm²', 'yd²'],
  Length: ['cm', 'ft', 'in', 'km', 'm', 'mi', 'mm', 'nmi', 'sxi', 'yd', 'µm'],
};

export interface ColumnConfig {
  visible?: boolean;
  order?: number;
  width?: number;
}

export interface ImpactTotals {
  [metricId: string]: number;
}

export interface ImpactReportColumnConfiguration {
  [columnId: string]: ColumnConfig;
}

export const DAILY_FREQUENCY = '1';
export const WEEKLY_FREQUENCY = '7';
export const MONTHLY_FREQUENCY = '30.5';
export const QUARTERLY_FREQUENCY = '90';
export const YEARLY_FREQUENCY = '365';

export type ImpactReportFrequencies =
  | typeof DAILY_FREQUENCY
  | typeof WEEKLY_FREQUENCY
  | typeof MONTHLY_FREQUENCY
  | typeof QUARTERLY_FREQUENCY
  | typeof YEARLY_FREQUENCY;
